import React, { useEffect, useCallback, useState } from "react";
import { Box, Typography } from "@material-ui/core";
import {
  CustomButton,
  CustomDialog,
  CustomInput,
  CustomRadioButtonGroupItem,
  ReskinningAutocomplete,
  ReskinningDatePicker,
  ReskinningInputs,
} from "../../../../../Components";

import useStyles from "./styles";
import { useSelectedTheme, useTranslate } from "../../../../../Hooks";
import { CreateInvoice, lookupItemsGetId } from "../../../../../Services";
import { StaticLookupsIds } from "../../../../../assets/json/StaticLookupsIds";
import { formatNumberWithCommas } from "../../../../../Helper";
import { useVerticalNav } from "../../../../../Contexts/VerticalNavContext";

const defaultFormValues = {
  unitId: null,
  amountExTax: 0,
  paymentTypeId: null,
  paymentModeId: null,
  invoiceContactTypeId: null,
  tax: 0,
  taxPercentage: 0,
  amountDue: 0,
  amountDuePercentage: 0,
  remarks: null,
  dueOn: null,
  invoiceStatus: false,
  depositSlipNo: null,
  chequeNo: null,
  bankId: null,
  branch: null,
  bondDetails: null,
  draftNo: null,
  creditCardNo: null,
  bankTransferNo: null,
  depositNo: null,
  invoiceStateId: 2,
};

function InvoiceDialog({
  tranActionDetails,
  onClose = () => {},
  refetch,
  open,
}) {
  const { translate } = useTranslate("afterSalesClientProfileView");
  const styles = useStyles();
  const {
    theme: { palette },
  } = useSelectedTheme();

  const [loadings, setLoadings] = React.useState({
    paymentTypes: false,
    paymentModes: false,
    contactTypes: false,
    banks: false,
  });

  const [paymentTypes, setPaymentTypes] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [contactTypes, setContactTypes] = useState([]);
  const [banks, setBanks] = useState([]);
  const [formValues, setFormValues] = useState(defaultFormValues);
  const [selectedTransaction, setSelectedTransaction] = React.useState(null);
  const [error, setError] = React.useState({});

  const { setAlertBoxContent } = useVerticalNav();

  const validateFields = () => {
    if (selectedTransaction) {
      const errors = {};
      if (!selectedTransaction)
        errors.unitRefNumber = "unit reference number is required";

      if (!formValues.invoiceContactTypeId)
        errors.invoiceContactTypeId = "contact type is required";
      if (!formValues.paymentModeId)
        errors.paymentModeId = "payment mode is required";
      if (!formValues.paymentTypeId)
        errors.paymentTypeId = "payment type is required";
      if (!formValues.dueOn) errors.dueOn = "Due on Date is required";

      setError(errors);
      return Object.keys(errors).length > 0;
    }
  };

  useEffect(() => {
    validateFields();
  }, [formValues]);
  const getAllPaymentTypes = useCallback(async () => {
    setLoadings((items) => ({ ...items, paymentTypes: true }));
    const res = await lookupItemsGetId({
      lookupTypeId: StaticLookupsIds.PaymentType,
    });
    if (!(res && res.status && res.status !== 200)) setPaymentTypes(res || []);
    else setPaymentTypes([]);
    setLoadings((items) => ({ ...items, paymentTypes: false }));
  }, []);
  const getAllBanks = useCallback(async () => {
    setLoadings((items) => ({ ...items, banks: true }));
    const res = await lookupItemsGetId({
      lookupTypeId: StaticLookupsIds.Banks,
    });
    if (!(res && res.status && res.status !== 200)) setBanks(res || []);
    else setBanks([]);
    setLoadings((items) => ({ ...items, banks: false }));
  }, []);
  const getAllPaymentModes = useCallback(async () => {
    setLoadings((items) => ({ ...items, paymentModes: true }));
    const res = await lookupItemsGetId({
      lookupTypeId: StaticLookupsIds.PaymentMode,
    });
    if (!(res && res.status && res.status !== 200)) setPaymentModes(res || []);
    else setPaymentModes([]);
    setLoadings((items) => ({ ...items, paymentModes: false }));
  }, []);
  const getAllContactTypes = useCallback(async () => {
    setLoadings((items) => ({ ...items, contactTypes: true }));
    const res = await lookupItemsGetId({
      lookupTypeId: StaticLookupsIds.ContactTypes,
      lookupParentId: 430,
    });
    if (!(res && res.status && res.status !== 200)) setContactTypes(res || []);
    else setContactTypes([]);
    setLoadings((items) => ({ ...items, contactTypes: false }));
  }, []);

  useEffect(() => {
    if (open) {
      getAllPaymentTypes();
      getAllPaymentModes();
      getAllContactTypes();
      getAllBanks();
    }
  }, [open]);

  const saveHandler = useCallback(async () => {
    const res = await CreateInvoice({
      ...formValues,
      unitTransactionId: selectedTransaction?.unitTransactionId,
      unitId: selectedTransaction?.unitId,
    });
    if (!(res?.status && res.status !== 200)) {
      refetch();
      return setAlertBoxContent({
        display: true,
        variant: "success",
        title: translate("Invoice created successfully"),
        onClose: () => {
          setAlertBoxContent(null);
        },
      });
    }
    return setAlertBoxContent({
      display: true,
      variant: "error",
      title: translate("Error Creating invoice"),
      onClose: () => {
        setAlertBoxContent(null);
      },
    });
  }, [formValues]);

  const showFields = (paymentModeId) => {
    switch (paymentModeId) {
      case 20695:
        return (
          <>
            <ReskinningAutocomplete
              withoutSearchButton
              data={banks}
              labelValue={"Bank"}
              displayLabel={(item) => item.lookupItemName}
              multiple={false}
              getOptionSelected={(item, value) =>
                item.lookupItemId === value.lookupItemId
              }
              // selectedValues={value}
              onChange={(_e, value) => {
                setFormValues((prev) => ({
                  ...prev,
                  bankId: value?.lookupItemId || "",
                }));
              }}
            />
            <Box className={styles.containerInputSection}>
              <Typography className={styles.titleFiled}>Branch</Typography>
              <CustomInput
                onChange={(event) => {
                  setFormValues((prev) => ({
                    ...prev,
                    branch: event.target.value,
                  }));
                }}
                hasSearchIcon={false}
                CustomClass={styles.inputField}
              />
            </Box>
            <Box className={styles.containerInputSection}>
              <Typography className={styles.titleFiled}>
                Bank Transfer Number
              </Typography>
              <CustomInput
                onChange={(event) => {
                  setFormValues((prev) => ({
                    ...prev,
                    bankTransferNo: event.target.value,
                  }));
                }}
                hasSearchIcon={false}
                CustomClass={styles.inputField}
              />
            </Box>
          </>
        );
      case 20696:
        return (
          <>
            <ReskinningAutocomplete
              withoutSearchButton
              data={banks}
              labelValue={"Bank"}
              displayLabel={(item) => item.lookupItemName}
              multiple={false}
              getOptionSelected={(item, value) =>
                item.lookupItemId === value.lookupItemId
              }
              // selectedValues={value}
              onChange={(_e, value) => {
                setFormValues((prev) => ({
                  ...prev,
                  bankId: value?.lookupItemId || "",
                }));
              }}
            />
            <Box className={styles.containerInputSection}>
              <Typography className={styles.titleFiled}>Branch</Typography>
              <CustomInput
                onChange={(event) => {
                  setFormValues((prev) => ({
                    ...prev,
                    branch: event.target.value,
                  }));
                }}
                hasSearchIcon={false}
                CustomClass={styles.inputField}
              />
            </Box>
            <Box className={styles.containerInputSection}>
              <Typography className={styles.titleFiled}>
                Credit Card Number
              </Typography>
              <CustomInput
                onChange={(event) => {
                  setFormValues((prev) => ({
                    ...prev,
                    creditCardNo: event.target.value,
                  }));
                }}
                hasSearchIcon={false}
                CustomClass={styles.inputField}
              />
            </Box>
          </>
        );
      case 20697:
        return (
          <>
            <ReskinningAutocomplete
              withoutSearchButton
              data={banks}
              labelValue={"Bank"}
              displayLabel={(item) => item.lookupItemName}
              multiple={false}
              getOptionSelected={(item, value) =>
                item.lookupItemId === value.lookupItemId
              }
              // selectedValues={value}
              onChange={(_e, value) => {
                setFormValues((prev) => ({
                  ...prev,
                  bankId: value?.lookupItemId || "",
                }));
              }}
            />
            <Box className={styles.containerInputSection}>
              <Typography className={styles.titleFiled}>Branch</Typography>
              <CustomInput
                onChange={(event) => {
                  setFormValues((prev) => ({
                    ...prev,
                    branch: event.target.value,
                  }));
                }}
                hasSearchIcon={false}
                CustomClass={styles.inputField}
              />
            </Box>
            <Box className={styles.containerInputSection}>
              <Typography className={styles.titleFiled}>
                Draft Number
              </Typography>
              <CustomInput
                onChange={(event) => {
                  setFormValues((prev) => ({
                    ...prev,
                    draftNo: event.target.value,
                  }));
                }}
                hasSearchIcon={false}
                CustomClass={styles.inputField}
              />
            </Box>
          </>
        );
      case 20694:
        return (
          <>
            <ReskinningAutocomplete
              withoutSearchButton
              data={banks}
              labelValue={"Bank"}
              displayLabel={(item) => item.lookupItemName}
              multiple={false}
              getOptionSelected={(item, value) =>
                item.lookupItemId === value.lookupItemId
              }
              onChange={(_e, value) => {
                setFormValues((prev) => ({
                  ...prev,
                  bankId: value?.lookupItemId || "",
                }));
              }}
            />
            <Box className={styles.containerInputSection}>
              <Typography className={styles.titleFiled}>Branch</Typography>
              <CustomInput
                onChange={(event) => {
                  setFormValues((prev) => ({
                    ...prev,
                    branch: event.target.value,
                  }));
                }}
                hasSearchIcon={false}
                CustomClass={styles.inputField}
              />
            </Box>
            <Box className={styles.containerInputSection}>
              <Typography className={styles.titleFiled}>
                Cheque Number
              </Typography>
              <CustomInput
                onChange={(event) => {
                  setFormValues((prev) => ({
                    ...prev,
                    chequeNo: event.target.value,
                  }));
                }}
                hasSearchIcon={false}
                CustomClass={styles.inputField}
              />
            </Box>
          </>
        );
      case 20693:
        return (
          <Box className={styles.containerInputSection}>
            <Typography className={styles.titleFiled}>
              Deposit Slip Number
            </Typography>
            <CustomInput
              onChange={(event) => {
                setFormValues((prev) => ({
                  ...prev,
                  depositSlipNo: event.target.value,
                }));
              }}
              hasSearchIcon={false}
              CustomClass={styles.inputField}
            />
          </Box>
        );
      case 20698:
        return (
          <Box className={styles.containerInputSection}>
            <Typography className={styles.titleFiled}>
              Bond Deposit Details
            </Typography>
            <CustomInput
              onChange={(event) => {
                setFormValues((prev) => ({
                  ...prev,
                  bondDetails: event.target.value,
                }));
              }}
              hasSearchIcon={false}
              CustomClass={styles.inputField}
            />
          </Box>
        );
      case 20699:
        return (
          <>
            <ReskinningAutocomplete
              withoutSearchButton
              data={banks}
              labelValue={"Bank"}
              displayLabel={(item) => item.lookupItemName}
              multiple={false}
              getOptionSelected={(item, value) =>
                item.lookupItemId === value.lookupItemId
              }
              onChange={(_e, value) => {
                setFormValues((prev) => ({
                  ...prev,
                  bankId: value?.lookupItemId || "",
                }));
              }}
            />
            <Box className={styles.containerInputSection}>
              <Typography className={styles.titleFiled}>Branch</Typography>
              <CustomInput
                onChange={(event) => {
                  setFormValues((prev) => ({
                    ...prev,
                    branch: event.target.value,
                  }));
                }}
                hasSearchIcon={false}
                CustomClass={styles.inputField}
              />
            </Box>
            <Box className={styles.containerInputSection}>
              <Typography className={styles.titleFiled}>
                Bank Deposit Number
              </Typography>
              <CustomInput
                onChange={(event) => {
                  setFormValues((prev) => ({
                    ...prev,
                    depositNo: event.target.value,
                  }));
                }}
                hasSearchIcon={false}
                CustomClass={styles.inputField}
              />
            </Box>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <CustomDialog
      open={open}
      onClose={() => {
        onClose();
        setSelectedTransaction(null);
        setFormValues(defaultFormValues);
      }}
      title={"Add Invoice"}
      paperClassName={styles.dialogWrapper}
      showActions={false}
      content={
        <Box>
          <Box className={styles.bannerBox}>
            <Typography
              style={{
                fontWeight: "600",
                color: palette.text.brand_secondary,
              }}
            >
              {translate("selling-price")}
            </Typography>
            <Typography
              style={{
                fontWeight: "500",
                color: palette.text.brand_secondary,
              }}
            >
              {selectedTransaction?.price
                ? `${formatNumberWithCommas(selectedTransaction?.price)} AED`
                : "N/A"}
            </Typography>
          </Box>
          <Box className={styles.fieldsSection}>
            <ReskinningAutocomplete
              withoutSearchButton
              data={tranActionDetails?.result || []}
              labelValue={"Unit Ref Number *"}
              displayLabel={(item) => item.unitRefNumber}
              multiple={false}
              getOptionSelected={(item, value) =>
                item.unitRefNumber === value.unitRefNumber
              }
              onChange={(_e, value) => {
                setSelectedTransaction(value || "");
              }}
              filterOptions={(options, state) =>
                options.filter(
                  (option) =>
                    option.unitRefNumber
                      .toLowerCase()
                      .includes(state.inputValue.toLowerCase()) ||
                    option.unitRefNumber === state.unitRefNumber
                )
              }
              error={error.unitRefNumber}
              helperText={error.unitRefNumber}
              isWithError={!!error.unitRefNumber}
              forceError={!!error.unitRefNumber}
            />
            <Box width={"100%"} className={styles.containerInputSection}>
              <Typography
                style={{
                  fontWeight: "500",
                  color: palette.text.primary,
                  marginRight: "auto",
                  minWidth: "190px",
                }}
              >
                External Tax Amount
              </Typography>
              <CustomInput
                type="number"
                disabled={!selectedTransaction?.unitTransactionId}
                onChange={(event) => {
                  let value = +event.target?.value;
                  if (value > selectedTransaction?.price)
                    value = +selectedTransaction?.price;
                  const amountDue = +(value + formValues.tax);
                  const amountDuePercentage = selectedTransaction?.price
                    ? +(amountDue / selectedTransaction?.price) * 100
                    : 0;
                  const taxPerValue = value
                    ? (+formValues.tax / value) * 100
                    : 0;
                  setFormValues((prev) => ({
                    ...prev,
                    amountExTax: value,
                    amountDue,
                    amountDuePercentage,
                    taxPercentage: taxPerValue,
                  }));
                }}
                onKeyDown={(event) => {
                  const maxPrice = selectedTransaction?.price || 0;
                  // Allow backspace, delete, tab, escape, enter, and arrow keys
                  if (
                    [8, 9, 27, 13, 37, 38, 39, 40, 46].includes(event.keyCode)
                  ) {
                    return;
                  }
                  // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                  if (
                    event.ctrlKey &&
                    [65, 67, 86, 88].includes(event.keyCode)
                  ) {
                    return;
                  }
                  const newChar = event.key;
                  if (/\d/.test(newChar)) {
                    const newValue = +(event.target.value + newChar);
                    if (newValue > maxPrice) {
                      event.preventDefault();
                      setFormValues((prev) => ({
                        ...prev,
                        amountExTax: maxPrice,
                        amountDuePercentage: 100,
                      }));
                    }
                  }
                }}
                value={formValues?.amountExTax}
                hasSearchIcon={false}
                CustomClass={styles.inputField}
              />
            </Box>
            <ReskinningAutocomplete
              withoutSearchButton
              data={paymentTypes}
              labelValue={"Payment Type *"}
              displayLabel={(item) => item.lookupItemName}
              multiple={false}
              getOptionSelected={(item, value) =>
                item.lookupItemId === value.lookupItemId
              }
              onChange={(_e, value) => {
                setFormValues((prev) => ({
                  ...prev,
                  paymentTypeId: value?.lookupItemId || "",
                }));
              }}
              isDisabled={!selectedTransaction?.unitTransactionId}
              error={error.paymentTypeId}
              helperText={error.paymentTypeId}
              isWithError={!!error.paymentTypeId}
              forceError={!!error.paymentTypeId}
            />
            <Box className={styles.taxWrapper}>
              <Typography
                style={{
                  fontWeight: "500",
                  color: palette.text.primary,
                  marginRight: "auto",
                }}
              >
                Tax
              </Typography>
              <Box style={{ display: "flex", gap: "16px", width: "430px" }}>
                <CustomInput
                  type="number"
                  disabled={!selectedTransaction?.unitTransactionId}
                  onChange={(event) => {
                    let value = +event.target?.value;
                    const effectedByValue = +(selectedTransaction?.price || 0);
                    if (value > effectedByValue) value = +effectedByValue;
                    const effectedValue =
                      +(value / formValues.amountExTax) * 100;
                    const amountDue = +(value + formValues.amountExTax);
                    const amountDuePercentage = +(
                      +(+amountDue / effectedByValue) * 100
                    );

                    setFormValues((prev) => ({
                      ...prev,
                      tax: value,
                      taxPercentage: +effectedValue,
                      amountDue,
                      amountDuePercentage,
                    }));
                  }}
                  value={formValues?.tax}
                  hasSearchIcon={false}
                  CustomClass={styles.inputField}
                />
                <CustomInput
                  type="number"
                  disabled={!selectedTransaction?.unitTransactionId}
                  onChange={(event) => {
                    let value = +event.target?.value;
                    if (value > 100) value = 100;

                    const taxValue = (+value / 100) * +formValues.amountExTax;
                    const amountDue = +(taxValue + formValues.amountExTax);
                    const amountDuePercentage = +(
                      +(amountDue / selectedTransaction?.price) * 100
                    );

                    setFormValues((prev) => ({
                      ...prev,
                      taxPercentage: +value,
                      tax: +taxValue,
                      amountDue,
                      amountDuePercentage,
                    }));
                  }}
                  value={formValues?.taxPercentage}
                  min={0}
                  max={100}
                  preInputText="%"
                  hasSearchIcon={false}
                  CustomClass={styles.inputField}
                />
              </Box>
            </Box>
            <ReskinningAutocomplete
              withoutSearchButton
              data={paymentModes}
              labelValue={"Payment Mode *"}
              displayLabel={(item) => item.lookupItemName}
              multiple={false}
              getOptionSelected={(item, value) =>
                item.lookupItemId === value.lookupItemId
              }
              // selectedValues={value}
              onChange={(_e, value) => {
                setFormValues((prev) => ({
                  ...prev,
                  paymentModeId: value?.lookupItemId || "",
                }));
              }}
              isDisabled={!selectedTransaction?.unitTransactionId}
              error={error.paymentModeId}
              helperText={error.paymentModeId}
              isWithError={!!error.paymentModeId}
              forceError={!!error.paymentModeId}
            />

            <Box className={styles.taxWrapper}>
              <Typography
                style={{
                  fontWeight: "500",
                  color: palette.text.primary,
                  marginRight: "auto",
                }}
              >
                Amount due
              </Typography>
              <Box style={{ display: "flex", gap: "16px", width: "430px" }}>
                <CustomInput
                  value={formValues?.amountDue}
                  onChange={(event) => {
                    let { value } = event.target;
                    const effectedByValue = +(selectedTransaction?.price || 0);
                    if (value > effectedByValue) value = effectedByValue;
                    const effectedValue = (value / effectedByValue) * 100;
                    setFormValues((prev) => ({
                      ...prev,
                      amountDue: value,
                      amountDuePercentage: effectedValue,
                    }));
                  }}
                  disabled
                  hasSearchIcon={false}
                  CustomClass={styles.inputField}
                />
                <CustomInput
                  type="number"
                  onChange={(event) => {
                    let { value } = event.target;
                    if (value > 100) value = 100;
                    const effectedValue =
                      (+(selectedTransaction?.price || 0) / 100) * value;

                    setFormValues((prev) => ({
                      ...prev,
                      amountDuePercentage: +value,
                      amountDue: effectedValue,
                    }));
                  }}
                  value={formValues?.amountDuePercentage}
                  min={0}
                  max={100}
                  preInputText="%"
                  disabled
                  hasSearchIcon={false}
                  CustomClass={styles.inputField}
                />
              </Box>
            </Box>
            <ReskinningDatePicker
              labelValue="Due on *"
              isDisabled={!selectedTransaction?.unitTransactionId}
              onDateChanged={(newValue) => {
                setFormValues((prev) => ({
                  ...prev,
                  dueOn: newValue,
                }));
              }}
              value={formValues?.dueOn}
              error={error.dueOn}
              helperText={error.dueOn}
              isWithError={!!error.dueOn}
              forceError={!!error.dueOn}
            />
            <ReskinningAutocomplete
              withoutSearchButton
              data={contactTypes}
              labelValue={"Contact Type *"}
              displayLabel={(item) => item.lookupItemName}
              multiple={false}
              getOptionSelected={(item, value) =>
                item.lookupItemId === value.lookupItemId
              }
              onChange={(_e, value) => {
                setFormValues((prev) => ({
                  ...prev,
                  invoiceContactTypeId: value?.lookupItemId || "",
                }));
              }}
              isDisabled={!selectedTransaction?.unitTransactionId}
              error={error.invoiceContactTypeId}
              helperText={error.invoiceContactTypeId}
              isWithError={!!error.invoiceContactTypeId}
              forceError={!!error.invoiceContactTypeId}
            />
            <Box className={styles.containerInputSection}>
              <Typography className={styles.titleFiled}>
                {translate("status")}
              </Typography>
              <Box className={styles.statusOptions}>
                <CustomRadioButtonGroupItem
                  checked={formValues?.invoiceStateId === 1}
                  onChange={() =>
                    setFormValues((prev) => ({
                      ...prev,
                      invoiceStateId: 1,
                    }))
                  }
                  label={
                    <Box
                      style={{
                        display: "flex",
                        alignItems: "center",
                        color: palette.utility.success_500,
                      }}
                    >
                      <ul style={{ marginBottom: "3px", fontSize: "14px" }}>
                        <li>{translate("paid")}</li>
                      </ul>
                    </Box>
                  }
                  value={"Paid"}
                />

                <CustomRadioButtonGroupItem
                  checked={formValues?.invoiceStateId === 2}
                  onChange={() =>
                    setFormValues((prev) => ({
                      ...prev,
                      invoiceStateId: 2,
                    }))
                  }
                  label={
                    <Box
                      style={{
                        display: "flex",
                        alignItems: "center",
                        color: palette.utility.error_500,
                      }}
                    >
                      <ul style={{ marginBottom: "3px", fontSize: "14px" }}>
                        <li>{translate("unpaid")}</li>
                      </ul>
                    </Box>
                  }
                  value={"Unpaid"}
                />
              </Box>
            </Box>
            <ReskinningInputs
              labelValue="Remarks"
              multiline
              rows={4}
              isDisabled={!selectedTransaction?.unitTransactionId}
              onInputChanged={(event) => {
                const value =
                  event && event.target
                    ? event.target.value
                    : event && event.currentTarget
                    ? event.currentTarget.value
                    : event;

                setFormValues((prev) => ({
                  ...prev,
                  remarks: value,
                }));
              }}
            />
            {showFields(formValues?.paymentModeId)}
          </Box>
          <Box className={styles.footerActions}>
            <CustomButton
              variant="outlined"
              color="secondary"
              onClick={onClose}
            >
              {translate("cancel")}
            </CustomButton>
            <CustomButton
              type="submit"
              variant="contained"
              color="primary"
              disabled={
                !selectedTransaction?.unitTransactionId ||
                Object.keys(error).length > 0
              }
              onClick={() => {
                const isWithError = validateFields();
                if (!isWithError) {
                  saveHandler();
                  onClose();
                  setFormValues(defaultFormValues);
                  setSelectedTransaction(null);
                } else {
                  setAlertBoxContent({
                    display: true,
                    variant: "error",
                    title: translate("please fill required fields"),
                    onClose: () => {
                      setAlertBoxContent(null);
                    },
                  });
                }
              }}
            >
              {translate("save")}
            </CustomButton>
          </Box>
        </Box>
      }
    />
  );
}

export default InvoiceDialog;
